import { VideoPlayer, VideoPlayerProps } from "@buildwitt/shapeshift-common/v1/videoPlayer";
import { useContext, useEffect, useRef, useState, forwardRef } from "react";
import { makeStyles } from "tss-react/mui";
import { ReactPlayerProps } from "react-player";
import { VideoPlayerRef } from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import { PlayerAPI } from "bitmovin-player/modules/bitmovinplayer-core";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";
import { LocaleContext } from "@/app2/shared/contexts";
import { VideoProviders } from "@buildwitt/shapeshift-common/v2/videoPlayer/types";
import { OnProgressProps } from "react-player/base";
import { brightcovePlayerConfig } from "@/constants";
import { useEventCallback } from "@mui/material";

/** Lesson shape needed for the video player (compatible with GetLessonForCourseResponse). */
export interface App2LessonVideoPlayerLesson {
	videoUrl?: string | null;
	videoType?: string | null;
	complete?: boolean;
	moduleThumbnailOverride?: { url?: string } | null;
}

/** Aligned with Bitmovin "video finished" window; Brightcove timeupdate `duration - ct < 10` uses 10. */
export const LESSON_VIDEO_NEAR_END_TOLERANCE_SEC = 5;
/** When resuming in the end zone, avoid seeking to exact EOF (buffer stall / native spinner on BC/HLS). */
export const LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC = 0.5;

const useStyles = makeStyles()((theme) => ({
	videoPlayerBackdrop: {
		position: "relative",
		width: "100%",
		paddingBottom: "56.25%",
		height: 0,
		overflow: "hidden",
		background: "black",
		boxSizing: "border-box",
		left: 0,
		top: 0,
		right: "0px",
		[theme.breakpoints.down("sm")]: {
			height: "250px"
		},
		"#bitmovin-player": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			objectFit: "cover"
		}
	},
	videoPlayer: {
		zIndex: 0,
		background: "black",
		position: "absolute",
		opacity: 0,
		left: 0,
		transition: "opacity .5s ease-in",
		right: "0px",
		height: "100% !important"
	},
	videoPlayerSpacer: {
		height: "550px",
		width: "100%",
		[theme.breakpoints.down("sm")]: {
			height: "250px"
		}
	}
}));

interface IPropsCustom {
	lesson?: App2LessonVideoPlayerLesson;
	bitmovinPlayer?: PlayerAPI | null;
	/**
	 * Brightcove: called on every valid `timeupdate` (not throttled). Used to persist
	 * `lastProgressRef` on navigate-away; `onProgress` is throttled to every 5s to limit socket load.
	 */
	onPlaybackTime?: (seconds: number) => void;
	componentOverrides?: {
		header?: JSX.Element;
	};
}

export type App2LessonVideoPlayerProps = Omit<ReactPlayerProps, "url"> & IPropsCustom;

const SKIP_MESSAGE = "Skipping is not permitted for incomplete lessons";

export const App2LessonVideoPlayer = forwardRef<VideoPlayerRef, App2LessonVideoPlayerProps>(
	function App2LessonVideoPlayer(
		{ componentOverrides, lesson, currentTime, onReady, bitmovinPlayer, onPlaybackTime, ...props },
		externalRef
	) {
		const { classes } = useStyles();
		const [videoReady, setVideoReady] = useState(false);
		const { notify } = useContext(App2NotificationContext);
		const [localeState] = useContext(LocaleContext);
		const internalRef = useRef<VideoPlayerRef>({});
		const bcMaxTime = useRef<number | null>(null);
		/** `onReady` subscribes once; player must read latest resume + callbacks. */
		const resumeTimeRef = useRef(currentTime);
		const onVideoFinishedRef = useRef(props.onVideoFinished);
		const onEndedRef = useRef(props.onEnded);
		const [player, setPlayer] = useState<unknown>();
		const hasCanPlayFired = useRef(false);

		resumeTimeRef.current = currentTime;
		onVideoFinishedRef.current = props.onVideoFinished;
		onEndedRef.current = props.onEnded;

		const locale = localeState?.locale;

		const cleanYouTubeUrl = (url: string): string => {
			if (!url || lesson?.videoType !== "url") return url;
			try {
				const urlObj = new URL(url);
				urlObj.searchParams.delete("list");
				urlObj.searchParams.delete("index");
				urlObj.searchParams.delete("playnext");
				return urlObj.toString();
			} catch {
				return url;
			}
		};

		const handleSaveProgress = useEventCallback((time: number): void => {
			props.onProgress?.({ playedSeconds: time });
		});

		const handleAllowComplete = useEventCallback((): void => {
			if (!lesson?.complete) {
				props.onVideoFinished?.();
			}
		});

		const isLessonComplete = useEventCallback((): boolean => {
			return !!lesson?.complete;
		});

		const handleReady: VideoPlayerProps["onReady"] = useEventCallback((extPlayer) => {
			onReady?.();
			setPlayer(extPlayer);

			// React (YouTube) player init
			((ytPlayer: unknown) => {
				if (!ytPlayer || lesson?.videoType !== "url") return;
				const internalYtPlayer = internalRef.current.react?.react?.getInternalPlayer?.();
				if (!internalYtPlayer) return;
				try {
					internalYtPlayer.addEventListener("onPlaybackRateChange", (event: MessageEvent) => {
						const newRate = event.data;
						if (newRate > 1.5) {
							internalYtPlayer.setPlaybackRate(1.5);
						}
					});
				} catch {
					// ignore
				}
			})(internalRef.current.react?.react?.player);

			// Brightcove player init
			((bcPlayer?: videojs.VideoJsPlayer) => {
				if (!bcPlayer?.player || lesson?.videoType !== "brightcove") return;

				const saveProgressThrottled = (() => {
					let lastCall = 0;
					return (time: number) => {
						const now = Date.now();
						if (now - lastCall >= 5000) {
							lastCall = now;
							handleSaveProgress(time);
						}
					};
				})();

				bcMaxTime.current = currentTime ?? null;

				bcPlayer.on("timeupdate", () => {
					const ct = bcPlayer.currentTime();
					let validCurrentTime = ct;
					const MAX_JUMP = 1;
					if (!isLessonComplete() && ct > (bcMaxTime.current ?? 0) + MAX_JUMP) {
						validCurrentTime = bcMaxTime.current ?? 0;
						bcPlayer.currentTime(validCurrentTime);
						notify(SKIP_MESSAGE, "", App2NotificationSeverity.warning, 5000, false);
						return;
					}
					bcMaxTime.current = Math.max(validCurrentTime, bcMaxTime.current ?? 0);
					onPlaybackTime?.(validCurrentTime);
					saveProgressThrottled(validCurrentTime);
					if (bcPlayer.duration() - ct < 10) {
						handleAllowComplete();
					}
				});

				bcPlayer.on("canplay", () => {
					if (hasCanPlayFired.current) return;
					hasCanPlayFired.current = true;
					bcPlayer.playbackRates([0.5, 0.75, 1, 1.25, 1.5]);
					const audioTrackList = bcPlayer.audioTracks?.();
					const tracks = audioTrackList ? Array.from(audioTrackList) : [];
					const userLang = (locale ?? "").split("-")[0].toLowerCase();
					if (!tracks.length || !userLang) return;
					let matched = false;
					for (const track of tracks) {
						const trackLang = (track.language ?? "").split("-")[0].toLowerCase();
						const isMatch = !matched && trackLang === userLang;
						track.enabled = isMatch;
						if (isMatch) matched = true;
					}
				});

				let firstPlay = true;
				bcPlayer.on("play", () => {
					if (!firstPlay) {
						return;
					}
					firstPlay = false;
					const duration = bcPlayer.duration();
					const resume = resumeTimeRef.current;
					const resumeSec = resume ?? 0;
					if (
						Number.isFinite(duration) &&
						duration > 0 &&
						resume != null &&
						resumeSec >= duration - LESSON_VIDEO_NEAR_END_TOLERANCE_SEC
					) {
						const seekTo = Math.max(0, Math.min(resumeSec, duration - LESSON_VIDEO_SEEK_AT_END_OFFSET_SEC));
						bcPlayer.currentTime(seekTo);
						bcPlayer.pause();
						try {
							bcPlayer.removeClass?.("vjs-waiting");
							bcPlayer.addClass?.("vjs-ended");
						} catch {
							// ignore
						}
						onVideoFinishedRef.current?.();
						onEndedRef.current?.();
						return;
					}
					bcPlayer.currentTime(resumeSec);
				});
			})(extPlayer);

			setVideoReady(true);
		});

		useEffect(() => {
			setVideoReady(false);
		}, [lesson?.videoUrl]);

		// Bitmovin restore
		useEffect(() => {
			if (videoReady && lesson?.videoType === "bitmovin" && currentTime && bitmovinPlayer) {
				bitmovinPlayer.seek(currentTime);
			}
		}, [currentTime, videoReady, bitmovinPlayer, lesson?.videoType]);

		// React player restore
		useEffect(() => {
			if (!internalRef.current.react || lesson?.videoType !== "url") return;
			const playerInstance = internalRef.current.react.react?.player;
			if (videoReady && playerInstance && currentTime) {
				playerInstance.seekTo(currentTime, "seconds");
				playerInstance.player?.play();
			}
		}, [currentTime, videoReady, lesson?.videoType]);

		const generateVideoRef = function <T extends { [x: string]: NonNullable<T> }>(
			current: T,
			type: VideoProviders
	): void | { [x: string]: NonNullable<T> } | null {
			if (type !== "loom") internalRef.current[type] = current;
			if (current && externalRef) {
				const refReturn =
					typeof externalRef === "function" ? externalRef(current) : (externalRef.current = current);
				if (refReturn != null) {
					return refReturn !== undefined && refReturn !== null ? { [type]: refReturn as NonNullable<T> } : null;
				}
			}
			return null;
		};

		// Bitmovin seek guard
		const handleBitmovinSeek = useEventCallback(
			(time: number, issuer: string | undefined, originalSeek: (t: number, i?: string) => boolean): boolean => {
				if (!issuer || !lesson || !videoReady || !bitmovinPlayer || lesson.complete || currentTime === time) {
					return originalSeek(time, issuer);
				}
				const diff = time - bitmovinPlayer.getCurrentTime();
				if (diff < 0) return originalSeek(time, issuer);
				notify(SKIP_MESSAGE, "", App2NotificationSeverity.warning, 5000, false);
				return false;
			}
		);

		useEffect(() => {
			if (!bitmovinPlayer) return;
			const originalSeek = bitmovinPlayer.seek.bind(bitmovinPlayer);
			bitmovinPlayer.seek = (time: number, issuer?: string): boolean => {
				return handleBitmovinSeek(time, issuer, originalSeek);
			};
			return () => {
				bitmovinPlayer.seek = originalSeek;
			};
		}, [bitmovinPlayer, lesson?.complete, videoReady]);

		const prevPlayedSeconds = useRef<number>(0);
		useEffect(() => {
			prevPlayedSeconds.current = 0;
		}, [lesson?.videoUrl]);

		const handleSeek = ({ playedSeconds }: OnProgressProps): void => {
			if (!internalRef.current.react) return;
			const playerInstance = internalRef.current.react.react?.player;
			if (!lesson || !videoReady || !playerInstance) return;

			const diff = playedSeconds - (prevPlayedSeconds.current ?? 0);
			if (prevPlayedSeconds.current > 0 && diff > 2 && !lesson.complete) {
				notify(SKIP_MESSAGE, "", App2NotificationSeverity.warning, 5000, false);
				playerInstance.seekTo(prevPlayedSeconds.current, "seconds");
				playerInstance.player?.play();
			} else {
				prevPlayedSeconds.current = playedSeconds;
			}
			if ((playerInstance.getDuration() ?? 0) - playedSeconds < 10) {
				props.onVideoFinished?.();
			}
			props.onProgress?.({ playedSeconds });
		};

		if (!lesson?.videoUrl) return <></>;

		return (
			<div className={classes.videoPlayerBackdrop}>
				{componentOverrides?.header}
				<VideoPlayer
					{...props}
					moduleVideoType={lesson.videoType as "bitmovin" | "url" | "brightcove" | "loom"}
					ref={
						lesson.videoType === "bitmovin"
							? externalRef
							: (current: unknown) => generateVideoRef(current as { [x: string]: NonNullable<unknown> }, "react")
					}
					onReady={handleReady}
					className={classes.videoPlayer}
					style={{ opacity: videoReady ? 1 : 0 }}
					url={cleanYouTubeUrl(lesson.videoUrl)}
					controls={true}
					providerDetails={{
						bitmovin: { player: bitmovinPlayer },
						brightcove: { ...brightcovePlayerConfig, posterUrl: lesson.moduleThumbnailOverride?.url }
					}}
					onProgress={lesson.videoType === "url" ? handleSeek : undefined}
				/>
			</div>
		);
	}
);
